import { apiFetch, walletJwtStorage } from './fetcher';

/**
 * Auth helpers. Two auth shapes:
 *
 *   - OAuth: cookie is HttpOnly, JS can't read it. We hit GET /v1/account
 *     with credentials and treat a 200 as proof of session.
 *   - Wallet: JWT sits in sessionStorage under `vfWalletJwt` and rides as
 *     `Authorization: Bearer <jwt>`. The SPA can read it.
 *
 * `getSession` returns a discriminated union so callers branch on
 * `authType` cleanly.
 */

export type Session =
  | {
      authType: 'oauth';
      customerId: string;
      email: string;
    }
  | {
      authType: 'wallet';
      walletAddress: string;
    };

interface AccountResponse {
  accountId: string;
  email: string;
}

function decodeWalletJwtAddress(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
    const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as {
      sub?: string;
      walletAddress?: string;
    };
    return payload.walletAddress ?? payload.sub ?? null;
  } catch {
    return null;
  }
}

export async function getSession(): Promise<Session | null> {
  // Wallet JWT path: a successful /v1/account proves the JWT is valid;
  // the address comes from the JWT itself (the SDK middleware that
  // authenticated the request keyed on it).
  const walletJwt = walletJwtStorage.read();
  if (walletJwt) {
    const address = decodeWalletJwtAddress(walletJwt);
    if (!address) {
      walletJwtStorage.clear();
      return null;
    }
    try {
      await apiFetch<AccountResponse>('/v1/account', { noAuthRedirect: true });
      return { authType: 'wallet', walletAddress: address };
    } catch {
      walletJwtStorage.clear();
      return null;
    }
  }

  // OAuth path: probe /v1/account with credentials. H6.9 loosened the
  // route to accept session cookies, so this resolves on cold reload
  // for any OAuth-signed-in customer. The rs_session HttpOnly cookie
  // rides automatically thanks to fetcher.ts's `credentials: 'include'`
  // default.
  try {
    const account = await apiFetch<AccountResponse>('/v1/account', {
      noAuthRedirect: true,
    });
    return {
      authType: 'oauth',
      customerId: account.accountId,
      email: account.email,
    };
  } catch {
    return null;
  }
}

export async function signOut(): Promise<void> {
  walletJwtStorage.clear();
  try {
    await apiFetch('/auth/signout', {
      method: 'POST',
      noAuthRedirect: true,
    });
  } catch {
    // signout failure on the cookie path is non-fatal — cookie clear is
    // best-effort and the user is already heading away.
  }
}

interface ChallengeResponse {
  nonce: string;
  message: string;
  expiresAt: number;
}

interface VerifyResponse {
  token: string;
  expiresIn: number;
}

export async function mintWalletJwt(args: {
  walletAddress: string;
  signMessage: (message: string) => Promise<string>;
}): Promise<{ token: string; expiresAt: number }> {
  const challenge = await apiFetch<ChallengeResponse>(
    `/v1/auth/challenge?wallet=${encodeURIComponent(args.walletAddress)}`,
    { noAuthRedirect: true },
  );
  const signature = await args.signMessage(challenge.message);
  const verified = await apiFetch<VerifyResponse>('/v1/auth/verify', {
    method: 'POST',
    body: JSON.stringify({
      walletAddress: args.walletAddress,
      nonce: challenge.nonce,
      signature,
    }),
    noAuthRedirect: true,
  });
  const expiresAt = Math.floor(Date.now() / 1000) + verified.expiresIn;
  walletJwtStorage.write(verified.token, expiresAt);
  return { token: verified.token, expiresAt };
}
